import { useEffect, useState } from "react";
import { FolderPlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FolderPicker } from "@/components/FileJumpSection";

/* A list of FileJump folders with Add/Remove chips and a scan button. Shared by the Music and Audiobook folder cards.
   Stored values are sent back verbatim; a newly picked path goes in `picked` so the server can tell inside/outside the music root apart. */
export const FolderSection = ({ id, icon, title, description, endpoint, onSaved, scanButton, creds, testid }) => {
  const [folders, setFolders] = useState(null);
  const [root, setRoot] = useState("");
  const [pick, setPick] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(endpoint).then((r) => { setFolders(r.data.folders); setRoot(r.data.root || ""); }).catch(() => setFolders([])); }, [endpoint]);
  const save = async (next, picked = []) => {
    setBusy(true);
    try {
      const { data } = await api.put(endpoint, { folders: next, picked });
      setFolders(data.folders);
      onSaved?.(data, picked);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div id={id} className="rounded-md bg-zinc-50 px-4 py-3" data-testid={testid}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <span>
          <span className="flex items-center gap-2 text-sm font-medium">{icon}{title}</span>
          <span className="text-xs text-zinc-500">{description}</span>
        </span>
        <span className="flex flex-wrap gap-2">
          {scanButton}
          <button onClick={() => setPick((v) => !v)} disabled={busy} data-testid={`${testid}-add`} className="flex h-8 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-3 text-xs hover:border-zinc-900 disabled:opacity-50"><FolderPlus size={13} />Add folder</button>
        </span>
      </div>
      {folders === null ? <Loader2 size={14} className="mt-3 animate-spin text-zinc-400" /> : folders.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2" data-testid={`${testid}-list`}>
          {folders.map((f) => (
            <li key={f} className="flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1 font-mono text-xs" data-testid={`${testid}-item-${f}`}>
              {f === "." ? <span title="Your whole FileJump music root">{root || "music root"}/ <span className="ml-1 rounded bg-zinc-200 px-1 text-[10px] text-zinc-700">music root</span></span>
                : f.startsWith("/") ? <span title="Outside the music root (path from the FileJump root)">{f.slice(1)}/ <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800">outside root</span></span> : `${f}/`}
              <button onClick={() => save(folders.filter((x) => x !== f))} disabled={busy} aria-label={`Remove ${f}`} data-testid={`${testid}-remove-${f}`} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
            </li>
          ))}
        </ul>
      )}
      {pick && <div className="mt-3"><FolderPicker creds={creds} value={folders?.[0] || ""} onPick={(path) => { setPick(false); if (path) save(folders || [], [path]); }} onClose={() => setPick(false)} /></div>}
    </div>
  );
};
